import { DataTable } from "@/components/DataTable/dataTable.component";
import TranslatedErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import NoteHoverCard from "@/components/NoteHoverCard/noteHoverCard.component";
import FiltersOrderTracking from "@/components/OrderTrackingTable/filtersOrderTracking.component";
import SingleOrderItemsTable from "@/components/OrderTrackingTable/singleOrderItemsTable.component";
import { fetchAllUsersOrders } from "@/models/socket/orders.model";
import { Button } from "@/shadcn/components/ui/button";
import { SimpleOrder } from "@/types";
import { formatDatePretty } from "@/utils/utils.util";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown, Eye } from "lucide-react";
import React, { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useLocation } from "react-router";

type OrderTrackingTableProps = {
  // TODO: add props here
};

const OrderTrackingTable: React.FC<OrderTrackingTableProps> = (props) => {
  const location = useLocation();
  const [orders, setOrders] = useState<SimpleOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [viewingOrderID, setViewingOrderID] = useState<string | null>(null);
  const [filteredOrders, setFilteredOrders] = useState<SimpleOrder[]>([]);

  const [filteredShipped, setFilteredShipped] = useState<
    "all" | SimpleOrder["shipped"]
  >("all");
  const [filteredAcceptedDate, setFilteredAcceptedDate] = useState<
    DateRange | undefined
  >(undefined);
  const navFilter = location.state?.filter;
  const [filteredStatus, setFilteredStatus] = useState<
    "all" | SimpleOrder["status"]
  >(navFilter?.status ? navFilter.status : "all");
  const [filterCompany, setFilterCompany] = useState<string>("all");
  const [availableCompanies, setAvailableCompanies] = useState<string[]>([]);

  useEffect(() => {
    async function fetchOrders() {
      setLoading(true);
      setError(null);
      setAvailableCompanies([]);
      console.log("Fetching orders...");
      const response = await fetchAllUsersOrders();
      console.log(response);
      if (response.rStatus === "success") {
        setOrders(response.rData);
        // company_name
        const companies = Array.from(
          new Set(response.rData.map((quote) => quote.company_name))
        ).sort();
        setAvailableCompanies(companies);
      } else {
        setError(response.rData);
      }
      setLoading(false);
    }

    fetchOrders();
  }, []);

  const columns: ColumnDef<SimpleOrder>[] = [
    {
      header: "View",
      accessorKey: "view",
      cell: ({ row }) => {
        // Add view action at the end for user convenience
        return (
          <div
            onClick={() => {
              setViewingOrderID(row.original.id);
            }}
            aria-label={"View"}
          >
            <Eye className="h-5 cursor-pointer" />
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          VSB Job Number
          <ArrowUpDown />
        </Button>
      ),
      // header: "VSB Job Number",
      accessorKey: "vsb_job_number",
      enableSorting: true,
      enableHiding: false,
      size: 150,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          WO Number
          <ArrowUpDown />
        </Button>
      ),
      // header: "WO Number",
      accessorKey: "wo_number",
      enableSorting: true,
      enableHiding: false,
      size: 120,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Quote Number
          <ArrowUpDown />
        </Button>
      ),
      // header: "Quote Number",
      accessorKey: "quote_number",
      enableSorting: true,
      enableHiding: false,
      size: 130,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Customer Order Number
          <ArrowUpDown />
        </Button>
      ),
      // header: "Customer Order Number",
      accessorKey: "customer_order_number",
      enableSorting: true,
      enableHiding: false,
      size: 200,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Reference
          <ArrowUpDown />
        </Button>
      ),
      // header: "Reference",
      accessorKey: "reference",
      enableSorting: true,
      enableHiding: false,
      size: 320,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Customer Name
          <ArrowUpDown />
        </Button>
      ),
      // header: "Customer Name",
      accessorKey: "company_name",
      enableSorting: true,
      enableHiding: false,
      size: 200,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Account Number
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "account_number",
      enableSorting: true,
      enableHiding: false,
      size: 160,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    // {
    //   header: ({ column }) => (
    //     <Button
    //       variant="ghost"
    //       className="!bg-transparent !p-0"
    //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //     >
    //       User Name
    //       <ArrowUpDown />
    //     </Button>
    //   ),
    //   // header: "User Name",
    //   accessorKey: "user_name",
    //   enableSorting: true,
    //   enableHiding: false,
    //   cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    // },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Accepted Date
          <ArrowUpDown />
        </Button>
      ),
      // header: "Accepted Date",
      accessorKey: "approval_at",
      enableSorting: true,
      enableHiding: false,
      size: 130,
      // Format date for user readability, show N/A if missing
      cell: ({ cell }) => (
        <span>
          {cell.getValue()
            ? formatDatePretty(cell.getValue() as string, false, false)
            : "N/A"}
        </span>
      ),
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Finish Date
          <ArrowUpDown />
        </Button>
      ),
      // header: "Finish Date",
      accessorKey: "completed_at",
      enableSorting: true,
      enableHiding: false,
      size: 130,
      // Format date for user readability, show N/A if missing
      cell: ({ cell }) => (
        <span>
          {cell.getValue()
            ? formatDatePretty(cell.getValue() as string, false, false)
            : "N/A"}
        </span>
      ),
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Status
          <ArrowUpDown />
        </Button>
      ),
      // header: "Status",
      accessorKey: "status",
      enableSorting: true,
      enableHiding: false,
      size: 100,
      // Use status as is for clarity
      cell: ({ cell }) =>
        cell.getValue() === "in_progress" ? (
          <span className="">In Progress</span>
        ) : cell.getValue() === "completed" ? (
          <span className="">Completed</span>
        ) : (
          <span className="text-red-500">Unknown</span>
        ),
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Out
          <ArrowUpDown />
        </Button>
      ),
      // header: "Out",
      accessorKey: "shipped",
      enableSorting: true,
      enableHiding: false,
      size: 60,
      // Map Y/N to Yes/No for clarity
      cell: ({ cell }) => <span>{cell.getValue() === "Y" ? "Yes" : "No"}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Note
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "note",
      enableSorting: true,
      enableHiding: false,
      size: 80,
      // Show N/A for null/empty notes for consistency
      cell: ({ cell }) =>
        cell.getValue() ? (
          <NoteHoverCard note={cell.getValue() as string} />
        ) : null,
    },
    {
      // header: "Vulcan Contact",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Vulcan Contact
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "quoted_by_name",
      enableSorting: true,
      enableHiding: false,
      size: 150,
      // Use quoted_by_name as "Vulcan Contact" for business context
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      // header: "Invoice Number",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Invoice Number
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "invoice_number",
      enableSorting: true,
      enableHiding: false,
      size: 140,
      // Show N/A for null/empty invoice numbers
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      // header: "DN Issued",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          DN Issued
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "delivery_note_issued",
      enableSorting: true,
      enableHiding: false,
      size: 100,
      // Map Y/N to Yes/No for clarity
      cell: ({ cell }) => <span>{cell.getValue() === "Y" ? "Yes" : "No"}</span>,
    },
    {
      // header: "DN POD",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          DN POD
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "delivery_note_proof_of_delivery",
      enableSorting: true,
      enableHiding: false,
      size: 90,
      // Map Y/N to Yes/No for clarity
      cell: ({ cell }) => <span>{cell.getValue() === "Y" ? "Yes" : "No"}</span>,
    },
    {
      // header: "Collect/Deliver",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Collect/Deliver
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "shipment_method",
      enableSorting: true,
      enableHiding: false,
      size: 130,
      // Show shipment method as is for clarity
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      // header: "Job Progress",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Job Progress
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "job_progress",
      enableSorting: true,
      enableHiding: false,
      size: 120,
      // Show job progress as is for clarity
      cell: ({ cell }) => <span>{String(cell.getValue())}%</span>,
    },
  ];

  useEffect(() => {
    if (!orders || orders.length === 0) {
      setFilteredOrders([]);
      return;
    }

    const filtered = orders.filter((order) => {
      // Filter by shipped status
      if (filteredShipped !== "all" && order.shipped !== filteredShipped) {
        return false;
      }

      // Filter by accepted date range
      if (filteredAcceptedDate) {
        // Ensure approval_at is not null before creating a Date to prevent runtime errors
        const acceptedDate = order.approval_at
          ? new Date(order.approval_at)
          : null;
        const fromDate = filteredAcceptedDate.from
          ? new Date(filteredAcceptedDate.from)
          : null;
        const toDate = filteredAcceptedDate.to
          ? new Date(filteredAcceptedDate.to)
          : null;

        // Ensure acceptedDate is not null before comparing, to avoid runtime errors
        if (
          !acceptedDate ||
          (fromDate && acceptedDate < fromDate) ||
          (toDate && acceptedDate > toDate)
        ) {
          return false;
        }
      }

      // filter by status
      if (filteredStatus !== "all" && order.status !== filteredStatus) {
        return false;
      }

      // Company filter
      if (filterCompany && filterCompany !== "all") {
        return order.company_name === filterCompany;
      }

      return true;
    });

    setFilteredOrders(filtered);
  }, [
    orders,
    filteredShipped,
    filteredAcceptedDate,
    filteredStatus,
    filterCompany,
  ]);

  function resetTableFilters() {
    setFilteredShipped("all");
    setFilteredAcceptedDate(undefined);
    setFilteredStatus("all");
    setFilterCompany("all");
  }

  useEffect(() => {
    // if the filters change then close the single view
    setViewingOrderID(null);
  }, [filteredShipped, filteredAcceptedDate, filteredStatus, filterCompany]);

  return loading ? (
    <div className="py-4">
      <div className="flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-3 border-white"></div>
      </div>
      <div className="flex items-center justify-center">
        <h2 className="mt-2 text-xl text-white">Loading Orders...</h2>
      </div>
    </div>
  ) : error ? (
    <div className="flex items-center justify-center">
      <p className="text-red-500">{error}</p>
    </div>
  ) : (
    <div className="mx-4">
      <DataTable
        columns={columns}
        data={filteredOrders}
        includeSelectRow={false}
        disableColumnSelection={true}
        // set the initial state to the approval_at, newest first
        initialState={{
          sorting: [
            {
              id: "approval_at",
              desc: true,
            },
          ],
        }}
        columnFiltersUI={
          <FiltersOrderTracking
            shipped={filteredShipped}
            setShipped={setFilteredShipped}
            acceptedDate={filteredAcceptedDate}
            setAcceptedDate={setFilteredAcceptedDate}
            status={filteredStatus}
            setStatus={setFilteredStatus}
            company={filterCompany}
            setCompany={setFilterCompany}
            availableCompanies={availableCompanies}
          />
        }
        resetTableFilters={resetTableFilters}
        resetFiltersDisabled={
          filteredStatus === "all" &&
          !filteredAcceptedDate &&
          filteredShipped === "all" &&
          filterCompany === "all"
        }
      />
      {viewingOrderID && (
        <TranslatedErrorBoundary>
          <SingleOrderItemsTable
            orderID={viewingOrderID}
            closeTable={() => setViewingOrderID(null)}
          />
        </TranslatedErrorBoundary>
      )}
    </div>
  );
};

export default OrderTrackingTable;
